import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NavigateFunction } from 'react-router-dom';

import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import {
  Velocidad,
  VelocidadesPagingRes,
} from '@/shared/interfaces/erp/system-settings';
import { useUiStore } from '@/store/ui';

const { get, post, put, remove } = erpApi();

///* tanSctack query
export const useFetchVelocidades = ({
  page = 1,
  nombre = '',
  page_size = 10,
}: GetVelocidadesParams) => {
  return useQuery({
    queryKey: ['velocidades', page, nombre],
    queryFn: async () => {
      return getVelocidades({ page, nombre, page_size });
    },
  });
};

export const useGetVelocidad = (id: string) => {
  return useQuery({
    queryKey: ['velocidades', id],
    queryFn: async () => {
      return getVelocidad(id);
    },
  });
};

export const useCreateVelocidad = (
  navigate: NavigateFunction,
  returnUrl: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createVelocidad,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['velocidades'] });
    },
    onSettled: () => {
      navigate(returnUrl);
    },
  });
};

export const useUpdateVelocidad = (
  navigate: NavigateFunction,
  returnUrl: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateVelocidad,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['velocidades'] });
    },
    onSettled: () => {
      navigate(returnUrl);
    },
  });
};

export const useRemoveVelocidad = () => {
  const queryClient = useQueryClient();
  const setConfirmDialogIsOpen = useUiStore.getState().setConfirmDialogIsOpen;

  return useMutation({
    mutationFn: removeVelocidad,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['velocidades'] });
    },
    onSettled: () => {
      setConfirmDialogIsOpen(false);
    },
  });
};

///* axios
export type GetVelocidadesParams = {
  page?: number;
  nombre?: string;
  page_size?: number;
};

export type CreateVelocidadParams = Omit<Velocidad, 'id_velocidad'>;
export type UpdateVelocidadParams = {
  id: string;
  data: CreateVelocidadParams;
};

export const getVelocidades = async (params?: GetVelocidadesParams) => {
  const { page = 1, nombre = '', page_size = 10 } = params || {};

  return get<VelocidadesPagingRes>(
    `${urlBackend}api/v1/velocidades/?page=${page}&nombre=${nombre}&page_size=${page_size}`,
    true
  );
};

export const getVelocidad = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await get<Velocidad>(`${urlBackend}api/v1/velocidades/${id}/`, true);

  res.status === 'error' && setAlert(true, res.message, res.status);
  return res;
};

export const createVelocidad = async (data: CreateVelocidadParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await post<Velocidad>(
    `${urlBackend}api/v1/velocidades/`,
    data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};

export const updateVelocidad = async ({ id, data }: UpdateVelocidadParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await put<Velocidad>(
    `${urlBackend}api/v1/velocidades/${id}/`,
    data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};

export const removeVelocidad = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await remove<Velocidad>(
    `${urlBackend}api/v1/velocidades/${id}/`,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};
